import React from "react";
import {
  GraduationCap,
  Train,
  Plane,
  Bus,
  ShoppingBag,
  MapPin,
  Sparkles,
  Check,
} from "lucide-react";
import { POPULAR_CAMPUS_HUBS } from "@/constants/locations";
import { cn } from "@/lib/utils";
import toast from "react-hot-toast";

const HUB_ICONS = {
  campus: GraduationCap,
  train: Train,
  plane: Plane,
  bus: Bus,
  mall: ShoppingBag,
  pin: MapPin,
};

const isHubMatching = (val, hub) => {
  if (!val || !val.trim()) return false;
  const cleanVal = val.trim().toLowerCase();
  const hubAddr = hub.address.toLowerCase();
  const hubLbl = hub.label.toLowerCase();
  return (
    cleanVal === hubAddr ||
    cleanVal === hubLbl ||
    hubAddr.startsWith(cleanVal) ||
    cleanVal.startsWith(hubAddr)
  );
};

/**
 * QuickHubs — renders a list of quick-tap location pills.
 *
 * Props:
 *  - onSelect(hub)       callback when a hub is selected
 *  - currentValue        currently selected value string
 *  - disabledValue       value that should be greyed-out (same-location guard)
 *  - className           extra classes on the outer wrapper
 *  - label               header label text (default "Quick Hubs")
 *  - compact             when true, skip the wrapper box/header and render bare pills
 *                        (used in SearchRide's shared 2-column container)
 */
export const QuickHubs = ({
  onSelect,
  currentValue = "",
  disabledValue = "",
  className = "",
  label = "Quick Hubs",
  compact = false,
}) => {
  const pills = POPULAR_CAMPUS_HUBS.map((hub) => {
    const Icon = HUB_ICONS[hub.icon] || MapPin;
    const isSelected = isHubMatching(currentValue, hub);
    const isDisabled = !isSelected && isHubMatching(disabledValue, hub);

    return (
      <button
        key={hub.id || hub.label}
        type="button"
        onClick={(e) => {
          if (isDisabled) {
            e.preventDefault();
            toast.error("Pickup and drop-off locations cannot be the same");
            return;
          }
          onSelect(hub);
        }}
        title={
          isDisabled
            ? "Cannot select the same pickup and drop-off location"
            : hub.address
        }
        className={cn(
          "group relative inline-flex items-center rounded-full font-medium transition-all duration-150 select-none",
          "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-amber-500/40",
          compact ? "gap-1.5 px-2.5 py-1 text-xs" : "gap-1.5 px-3 py-1.5 text-xs",
          isDisabled
            ? "opacity-35 cursor-not-allowed bg-zinc-100 text-zinc-400 border border-zinc-200/60"
            : isSelected
            ? "bg-amber-500 text-white border border-amber-500 shadow-xs ring-2 ring-amber-300/50 font-semibold cursor-pointer active:scale-95"
            : "bg-white hover:bg-amber-50/80 text-zinc-700 hover:text-amber-900 border border-zinc-200/90 hover:border-amber-300/90 shadow-2xs active:scale-95 cursor-pointer"
        )}
      >
        <Icon
          className={cn(
            "shrink-0 transition-colors",
            compact ? "w-3 h-3" : "w-3.5 h-3.5",
            isDisabled
              ? "text-zinc-300"
              : isSelected
              ? "text-white"
              : "text-zinc-400 group-hover:text-amber-600"
          )}
        />
        <span className={cn("tracking-tight", isDisabled && "line-through decoration-zinc-300")}>
          {hub.label}
        </span>
        {isSelected && (
          <Check
            className={cn(
              "text-white shrink-0 stroke-[2.5]",
              compact ? "w-3 h-3 ml-0.5" : "w-3.5 h-3.5 ml-0.5"
            )}
          />
        )}
      </button>
    );
  });

  // Compact mode: bare pills, no wrapper
  if (compact) {
    return (
      <div className={cn("flex flex-wrap gap-1.5", className)}>
        {pills}
      </div>
    );
  }

  // Default mode: wrapped with header + "tap to fill" label
  return (
    <div className={cn("mt-2.5 p-2.5 rounded-xl bg-zinc-50/90 border border-zinc-200/60 shadow-2xs space-y-2", className)}>
      <div className="flex items-center justify-between px-0.5">
        <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider select-none flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-amber-500" />
          {label}
        </span>
        <span className="text-[10px] text-zinc-400 font-medium select-none">
          tap to fill
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {pills}
      </div>
    </div>
  );
};

export default QuickHubs;
